/**
 * Permission Service
 * 
 * จัดการ Menu Permissions สำหรับ Admin
 * - เก็บ permissions เป็น array ของ menu paths
 * - Super Admin = ['all'] หรือไม่เก็บ (เห็นทุกอย่าง)
 * - Admin/Partner = array ของ paths ที่เข้าถึงได้
 */

import { AdminService } from '@/domain/admin/admin.service'
import { AdminGroup } from '@/domain/admin/admin.types'
import { MENU_PERMISSIONS } from '@/hooks/use-permission'

// ============================================
// Types
// ============================================

export interface MenuPermission {
  path: string
  label: string
  group?: string
}

export interface PermissionConfig {
  adminId: string
  permissions: string[] // Array ของ menu paths หรือ ['all'] สำหรับ Super Admin
}

// ============================================
// Permission Service
// ============================================

export const PermissionService = {
  /**
   * ดึง menu permissions สำหรับ admin
   */
  async getAdminPermissions(adminId: string): Promise<string[]> {
    try {
      const result = await AdminService.findOne(adminId)
      if (!result) return []
      return result.permissions || []
    } catch (error) {
      console.error('[PermissionService] Error getting permissions:', error)
      return []
    }
  },

  /**
   * อัพเดท menu permissions สำหรับ admin
   */
  async updateAdminPermissions(
    adminId: string,
    permissions: string[]
  ): Promise<void> {
    try {
      await AdminService.update(adminId, {
        permissions,
      })
    } catch (error) {
      console.error('[PermissionService] Error updating permissions:', error)
      throw error
    }
  },

  /**
   * สร้าง default permissions ตาม admin_group
   */
  getDefaultPermissions(adminGroup: AdminGroup): string[] {
    if (adminGroup === AdminGroup.SUPER_ADMIN) {
      return ['all'] // Super Admin เห็นทุกอย่าง
    }

    // ดึง permissions จาก MENU_PERMISSIONS config
    const allowedPaths: string[] = []
    
    Object.entries(MENU_PERMISSIONS).forEach(([path, allowedGroups]) => {
      if (allowedGroups.includes(adminGroup)) {
        allowedPaths.push(path)
      }
    })

    return allowedPaths
  },

  /**
   * ตรวจสอบว่า permissions ถูกต้องหรือไม่
   */
  validatePermissions(permissions: string[]): boolean {
    if (permissions.length === 0) return false
    if (permissions.includes('all')) return true // Super Admin

    // ตรวจสอบว่า paths ทั้งหมดอยู่ใน MENU_PERMISSIONS หรือไม่
    const validPaths = Object.keys(MENU_PERMISSIONS)
    return permissions.every(path => validPaths.includes(path))
  },

  /**
   * แปลง permissions array เป็น object สำหรับแสดงใน UI
   */
  permissionsToObject(permissions: string[]): Record<string, boolean> {
    const result: Record<string, boolean> = {}
    
    if (permissions.includes('all')) {
      // Super Admin - เปิดทุกอย่าง
      Object.keys(MENU_PERMISSIONS).forEach(path => {
        result[path] = true
      })
      return result
    }

    // ตั้งค่าเฉพาะ paths ที่มีใน permissions
    Object.keys(MENU_PERMISSIONS).forEach(path => {
      result[path] = permissions.includes(path)
    })

    return result
  },

  /**
   * แปลง object เป็น permissions array
   */
  objectToPermissions(obj: Record<string, boolean>): string[] {
    const permissions: string[] = []
    
    Object.entries(obj).forEach(([path, enabled]) => {
      if (enabled) {
        permissions.push(path)
      }
    })

    return permissions
  },
}
